/**
 * Sekretly — Image gradient palette.
 *
 * The canonical set of thumbnail backgrounds used behind itinerary imagery
 * (StopCard / HighlightCard) when no photo is present. Each entry is a
 * two-stop linear-gradient keyed by content category, plus its default
 * Tabler icon — per Sekretly Design System §7 "Image gradient palette".
 * Cards layer a soft radial highlight (and, on HighlightCard, a diagonal
 * stripe) over the gradient. A flat `swatch` colour is also provided for
 * contexts that cannot render a gradient (e.g. a solid-fill favicon-style
 * dot) and as a fallback while an image/gradient loads.
 *
 * Usage:
 *   import { IMAGE_GRADIENTS, gradientFor } from './imageGradients';
 *   <StopCard gradientKey="food" ... />
 */
export interface ImageGradient {
  /** Two-stop CSS linear-gradient — the primary value cards render. */
  base: string;
  /** Flat fallback colour (first stop) for non-gradient-capable contexts. */
  swatch: string;
  icon: string;
}

export const IMAGE_GRADIENTS: Record<string, ImageGradient> = {
  food:     { base: 'linear-gradient(150deg,#8a5b30,#33251a)', swatch: '#3d2a14', icon: 'tools-kitchen-2' },
  drink:    { base: 'linear-gradient(150deg,#7a2f3f,#2a161c)', swatch: '#2d1520', icon: 'glass' },
  view:     { base: 'linear-gradient(150deg,#3d4d70,#1a2030)', swatch: '#1a2030', icon: 'mountain' },
  nature:   { base: 'linear-gradient(150deg,#43603c,#1d281b)', swatch: '#1d281b', icon: 'plant-2' },
  landmark: { base: 'linear-gradient(150deg,#6b4a2f,#2a1e16)', swatch: '#2a1e16', icon: 'building-monument' },
  art:      { base: 'linear-gradient(150deg,#574a6b,#221c2a)', swatch: '#221c2a', icon: 'building-arch' },
  shop:     { base: 'linear-gradient(150deg,#5a4a6b,#241c2a)', swatch: '#241c2a', icon: 'shopping-bag' },
  travel:   { base: 'linear-gradient(150deg,#3a3f47,#1b1d22)', swatch: '#1b1d22', icon: 'train' },
};

export const IMAGE_GRADIENT_KEYS = Object.keys(IMAGE_GRADIENTS);

/** Resolve a category key to its { base, icon }. Falls back to 'food'. */
export function gradientFor(key: string): ImageGradient {
  return IMAGE_GRADIENTS[key] || IMAGE_GRADIENTS.food;
}

/** Flat identity colours (the gradient start tones) for seeded avatars. */
const IDENTITY_COLORS = Object.values(IMAGE_GRADIENTS).map(
  (g) => /\(150deg,(#[0-9a-fA-F]+),/.exec(g.base)?.[1] ?? g.swatch,
);

/**
 * Stable palette colour derived from any seed string (user/traveler id).
 * Used for identity-tinted avatars in dense host views (conversation lists,
 * kanban cards) where every traveler needs a distinct, deterministic colour.
 */
export function stableColorFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return IDENTITY_COLORS[h % IDENTITY_COLORS.length];
}
